import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { PublicHeader, PublicFooter, CATEGORY_META } from "@/pages/BlogPage";
import SharedFormattedMarkdown from "@/components/FormattedMarkdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

function embedYoutube(url) {
    // Convert youtu.be/xxx or youtube.com/watch?v=xxx to youtube.com/embed/xxx
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    return null;
}

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ author_name: "", author_email: "", body: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await publicApi.get(`/blog/posts/${slug}`);
                setPost(data);
                const cs = await publicApi.get(`/blog/posts/${data.id}/comments`);
                setComments(cs.data);
            } catch { setPost(false); }
            finally { setLoading(false); }
        })();
    }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await publicApi.post(`/blog/posts/${post.id}/comments`, { ...form, author_email: form.author_email?.trim() || null });
            toast.success("Comentário enviado!");
            setForm({ author_name: "", author_email: "", body: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Falha ao enviar");
        } finally { setSubmitting(false); }
    };

    if (loading) return (
        <div className="min-h-screen bg-base"><PublicHeader /><div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-brand" size={32} /></div></div>
    );
    if (post === false) return (
        <div className="min-h-screen bg-base"><PublicHeader />
            <div className="max-w-2xl mx-auto p-10 text-center">
                <h1 className="font-display text-2xl text-brand">Artigo não encontrado</h1>
                <Link to="/blog" className="btn-secondary mt-4 inline-flex">Voltar</Link>
            </div>
        </div>
    );

    const meta = CATEGORY_META[post.category] || CATEGORY_META.autocuidado;
    const Icon = meta.Icon;
    const embed = embedYoutube(post.video_url);

    return (
        <div className="min-h-screen bg-base">
            <PublicHeader />

            <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-20">
                <Link to="/blog" className="btn-ghost mb-4"><ArrowLeft size={14} /> Voltar</Link>

                <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${meta.color} mb-3`}>
                    <Icon size={11} /> {meta.label}
                </div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand tracking-tight leading-tight mb-3">{post.title}</h1>
                <div className="text-sm text-muted-clinical mb-6">
                    Por {post.author_name} · {new Date(post.created_at).toLocaleDateString("pt-BR")}
                </div>

                {post.cover_image_url && !embed && (
                    <div className="rounded-2xl overflow-hidden mb-8 aspect-[16/9] bg-alt">
                        <img src={post.cover_image_url.startsWith('/api') ? `${BACKEND_URL}${post.cover_image_url}` : post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                )}
                {embed && (
                    <div className="rounded-2xl overflow-hidden mb-8 aspect-video bg-black">
                        <iframe src={embed} title={post.title} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                )}
                {post.video_url && !embed && post.video_url.startsWith("/api") && (
                    <video controls src={`${BACKEND_URL}${post.video_url}`} className="w-full rounded-2xl mb-8 bg-black" />
                )}

                <div className="prose-clinical text-base leading-relaxed">
                    <FormattedMarkdown text={post.body} />
                </div>

                {/* Comments */}
                <div className="mt-16">
                    <div className="flex items-center gap-2 mb-6">
                        <MessageSquare size={18} className="text-brand" />
                        <h2 className="font-display text-2xl font-medium text-brand">Comentários ({comments.length})</h2>
                    </div>

                    <form onSubmit={submit} className="card-earthy mb-6">
                        <h3 className="font-display text-lg font-medium text-brand mb-3">Deixe seu comentário</h3>
                        <div className="grid md:grid-cols-2 gap-3 mb-3">
                            <input data-testid="comment-name" required placeholder="Seu nome" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="input-clinical" />
                            <input data-testid="comment-email" type="email" placeholder="E-mail (opcional)" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} className="input-clinical" />
                        </div>
                        <textarea data-testid="comment-body" required rows={4} placeholder="Sua mensagem..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-clinical mb-3" />
                        <button data-testid="comment-submit" disabled={submitting} className="btn-primary">
                            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar comentário
                        </button>
                    </form>

                    {comments.length === 0 ? (
                        <p className="text-sm text-muted-clinical">Seja o primeiro a comentar.</p>
                    ) : (
                        <div className="space-y-3">
                            {comments.map((c) => (
                                <div key={c.id} className="card-earthy">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <div className="font-medium text-brand">{c.author_name}</div>
                                        <div className="text-xs text-muted-clinical">{new Date(c.created_at).toLocaleDateString("pt-BR")}</div>
                                    </div>
                                    <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{c.body}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </article>

            <PublicFooter />
        </div>
    );
}

function FormattedMarkdown({ text }) {
    // Delegates to shared, DOMPurify-sanitized component (see components/FormattedMarkdown.jsx)
    // Kept as local alias so existing call sites don't change.
    return <SharedFormattedMarkdown text={text} />;
}
